import React, { type FC, useRef } from 'react';

import { Flex } from '@adsk/alloy-react-box';
import { ChevronDownIcon, ChevronUpIcon } from '@adsk/alloy-react-icon';
import Overlay from '@adsk/alloy-react-overlay';
import theme from '@adsk/alloy-react-theme';

import type { PreviewType } from '../../hooks/usePreview';
import { useRemoteOverrides } from '../../providers/RemoteOverridesProvider';
import i18n from '../../utils/i18n';
import OverridePanel from '../OverridePanel/OverridePanel';
import ToggleReactScan from '../ToggleReactScan/ToggleReactScan';

/**
 * Props for OverrideOverlay component
 */
interface OverrideOverlayProps {
  /** Preview data if available */
  preview?: PreviewType;
  /** Handler for reset exo-skeleton preview click */
  onPreviewResetClick: () => void;
  /** Handler for remote override reset click */
  onRemoteOverrideResetClick: (scope: string) => void;
}

/**
 * Overlay component for displaying preview and override controls
 */
const OverrideOverlay: FC<OverrideOverlayProps> = ({
  preview,
  onPreviewResetClick,
  onRemoteOverrideResetClick,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { overrides } = useRemoteOverrides();

  return (
    <Flex
      flexDirection="column"
      alignItems="flex-end"
      position="fixed"
      top={0}
      right={0}
      zIndex={theme.zIndex.modal + 5}
      ref={containerRef}
    >
      <Overlay
        placement="bottom"
        container={containerRef}
        renderTarget={({ targetProps, handleToggle, show }) => (
          <Flex
            {...targetProps}
            onClick={handleToggle}
            height="24px"
            textAlign="center"
            backgroundColor="pink500"
            padding="0 8px"
            justifyContent="center"
            alignItems="center"
            style={{
              cursor: 'pointer',
            }}
            typography="bodyMediumBold"
            data-testid="RemoteOverridesOverlay"
          >
            {i18n.t('DEV_DETAILS')}
            {show ? (
              <ChevronUpIcon style={{ marginLeft: 4 }} size={12} />
            ) : (
              <ChevronDownIcon style={{ marginLeft: 4 }} size={12} />
            )}
          </Flex>
        )}
        renderOverlay={({ overlayProps }) => (
          <Overlay.Container placement="bottom" {...overlayProps}>
            <Flex
              backgroundColor="white"
              style={{ boxShadow: theme.shadows.lowElevation }}
              flexDirection="column"
            >
              <ToggleReactScan />
              {preview && (
                <OverridePanel
                  title={preview.bucketPath}
                  onResetClick={onPreviewResetClick}
                  tooltipContent={preview.bucketId}
                  resetDataTestId="exit-preview"
                />
              )}
              {Object.keys(overrides).map((scope) => (
                <OverridePanel
                  key={scope}
                  onResetClick={() => onRemoteOverrideResetClick(scope)}
                  title={scope}
                  tooltipContent={overrides[scope]}
                  backgroundColor="green500"
                  resetDataTestId={`${scope}-reset`}
                />
              ))}
            </Flex>
          </Overlay.Container>
        )}
      />
    </Flex>
  );
};

export default OverrideOverlay;
